// ProfilePage.jsx
import React, { useState, useEffect } from 'react';
import { User, Award, Package, Heart, Home, ShoppingCart, Trash2, MapPin } from 'lucide-react';
import { useFavorites } from '../../context/FavoritesContext';
import { useCart } from '../../context/CartContext';
import './profile.css';
import { useNavigate } from "react-router-dom";
import {
  fetchProfileDashboard,
  updateProfile,
} from "../../api/profile.api";
const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const NAME_REGEX = /^[A-Za-z\s]+$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_NAME_LENGTH = 50;
const PHONE_LENGTH = 10;
const ADDRESS_LINE_MAX_LENGTH = 50;
const PINCODE_LENGTH = 6;
const PREORDER_STATUS_MAP = {
  submitted: {
    label: "Submitted",
    className: "processing",
  },
  approved: {
    label: "Approved",
    className: "delivered",
  },
  paid: {
    label: "Paid",
    className: "delivered",
  },
  pending: {
    label: "Pending",
    className: "processing",
  },
  rejected: {
    label: "Rejected",
    className: "rejected",
  },
};

const clearErrorForField = (errors, fieldName) => {
  if (!errors[fieldName]) return errors;

  const nextErrors = { ...errors };
  delete nextErrors[fieldName];
  return nextErrors;
};

const normalizePhoneForProfileDisplay = (value) => {
  const digits = String(value || "").replace(/\D/g, "");

  if (digits.length > PHONE_LENGTH && digits.endsWith(digits.slice(-PHONE_LENGTH))) {
    return digits.slice(-PHONE_LENGTH);
  }

  if (digits.length >= PHONE_LENGTH) {
    return digits.slice(-PHONE_LENGTH);
  }

  return digits;
};

const isValidPastDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) {
    return false;
  }

  const [year, month, day] = value.split("-").map(Number);
  const parsedDate = new Date(year, month - 1, day);

  if (
    parsedDate.getFullYear() !== year ||
    parsedDate.getMonth() !== month - 1 ||
    parsedDate.getDate() !== day
  ) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return parsedDate <= today;
};

const validatePersonalInfo = (data = {}) => {
  const errors = {};
  const firstName = String(data.first_name || "").trim();
  const lastName = String(data.last_name || "").trim();
  const email = String(data.email || "").trim();
  const phone = String(data.phone_number || "").trim();
  const dateOfBirth = String(data.date_of_birth || "").trim();

  if (!firstName) {
    errors.first_name = "First name is required";
  } else if (firstName.length > MAX_NAME_LENGTH) {
    errors.first_name = `First name must be ${MAX_NAME_LENGTH} letters or less`;
  } else if (!NAME_REGEX.test(firstName)) {
    errors.first_name = "First name should contain letters only";
  }

  if (!lastName) {
    errors.last_name = "Last name is required";
  } else if (lastName.length > MAX_NAME_LENGTH) {
    errors.last_name = `Last name must be ${MAX_NAME_LENGTH} letters or less`;
  } else if (!NAME_REGEX.test(lastName)) {
    errors.last_name = "Last name should contain letters only";
  }

  if (!email) {
    errors.email = "Email is required";
  } else if (!EMAIL_REGEX.test(email)) {
    errors.email = "Enter a valid email address";
  }

  if (!phone) {
    errors.phone_number = "Phone number is required";
  } else if (!/^\d+$/.test(phone)) {
    errors.phone_number = "Phone number should contain numbers only";
  } else if (phone.length !== PHONE_LENGTH) {
    errors.phone_number = `Phone number must be ${PHONE_LENGTH} digits`;
  }

  if (!dateOfBirth) {
    errors.date_of_birth = "Date of birth is required";
  } else if (!isValidPastDate(dateOfBirth)) {
    errors.date_of_birth = "Enter a valid date of birth";
  }

  return errors;
};

const validateAddressInfo = (data = {}) => {
  const errors = {};
  const addressLine1 = String(data.address_line1 || "").trim();
  const addressLine2 = String(data.address_line2 || "").trim();
  const state = String(data.state || "").trim();
  const city = String(data.city || "").trim();
  const pincode = String(data.pincode || "").trim();

  if (!addressLine1) {
    errors.address_line1 = "Address line 1 is required";
  } else if (addressLine1.length > ADDRESS_LINE_MAX_LENGTH) {
    errors.address_line1 = `Address line 1 must be ${ADDRESS_LINE_MAX_LENGTH} characters or less`;
  }

  if (addressLine2.length > ADDRESS_LINE_MAX_LENGTH) {
    errors.address_line2 = `Address line 2 must be ${ADDRESS_LINE_MAX_LENGTH} characters or less`;
  }

  if (!state) {
    errors.state = "State is required";
  }

  if (!city) {
    errors.city = "City is required";
  }

  if (!pincode) {
    errors.pincode = "Pincode is required";
  } else if (!/^\d+$/.test(pincode)) {
    errors.pincode = "Pincode should contain numbers only";
  } else if (pincode.length !== PINCODE_LENGTH) {
    errors.pincode = `Pincode must be ${PINCODE_LENGTH} digits`;
  }

  return errors;
};

const ProfilePage = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { toggleFavorite } = useFavorites();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const { favorites, removeFromFavorites } = useFavorites();
  const [isAddressEditing, setIsAddressEditing] = useState(false);
  const [addressEditData, setAddressEditData] = useState({});
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [errors, setErrors] = useState({});

  const {
    updateCart,
    getProductQuantity,
    isProductInCart,
  } = useCart();

  const navigate = useNavigate();

  const [showCopied, setShowCopied] = useState(false);

  const handlePersonalFieldChange = (fieldName, value) => {
    let nextValue = value;

    if (fieldName === "first_name" || fieldName === "last_name") {
      nextValue = value.replace(/[^A-Za-z\s]/g, "").slice(0, MAX_NAME_LENGTH);
    }

    if (fieldName === "phone_number") {
      nextValue = value.replace(/\D/g, "").slice(0, PHONE_LENGTH);
    }

    setEditData((prev) => ({
      ...prev,
      [fieldName]: nextValue,
    }));

    setFieldErrors((prev) => clearErrorForField(prev, fieldName));
  };

  const handleAddressFieldChange = (fieldName, value) => {
    let nextValue = value;

    if (fieldName === "address_line1" || fieldName === "address_line2") {
      nextValue = value.slice(0, ADDRESS_LINE_MAX_LENGTH);
    }

    if (fieldName === "pincode") {
      nextValue = value.replace(/\D/g, "").slice(0, PINCODE_LENGTH);
    }

    setAddressEditData((prev) => ({
      ...prev,
      [fieldName]: nextValue,
    }));

    setErrors((prev) => clearErrorForField(prev, fieldName));
  };

  /* -------------------- FETCH PROFILE -------------------- */
  useEffect(() => {
    let mounted = true;

    fetchProfileDashboard()
      .then((data) => {
        if (!mounted) return;
        setProfileData(data);
      })
      .catch(() => {
        if (!mounted) return;
        setError("Unable to load profile data");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);
  /* -------------------- FETCH STATES -------------------- */
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await fetch(`${BASE_URL}/enduser/states/`);
        const data = await res.json();
        setStates(data);
      } catch (err) {
        console.error("Failed to load states", err);
      }
    };

    fetchStates();
  }, []);

  const saveAddress = async () => {
    try {
      const validationErrors = validateAddressInfo(addressEditData);

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setSaving(true);
      setSaveError("");
      setErrors({});

      const res = await fetch(
        `${BASE_URL}/enduser/address/update/`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            address_line1: addressEditData.address_line1,
            address_line2: addressEditData.address_line2,
            landmark: addressEditData.landmark,
            state: addressEditData.state,
            city: addressEditData.city,
            pincode: addressEditData.pincode,
          }),
        }
      );

      if (!res.ok) {
        throw new Error("Failed to update address");
      }

      const data = await res.json();


      setProfileData(prev => ({
        ...prev,
        default_address: data.default_address,
      }));

      setIsAddressEditing(false);
      setErrors({});
      setToastMessage("Address updated successfully ✨");

      setTimeout(() => setToastMessage(""), 3000);

    } catch (err) {
      console.error(err);
      setSaveError("Unable to save address. Please try again.");
    } finally {
      setSaving(false);
    }
  };
  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 1800);
  };
  const ORDER_STATUS_MAP = {
    Pending: {
      label: "Pending",
      className: "processing",
    },
    Confirmed: {
      label: "Confirmed",
      className: "delivered",
    },
    Shipped: {
      label: "Shipped",
      className: "shipped",
    },
    Completed: {
      label: "Delivered",
      className: "delivered",
    },
    Rejected: {
      label: "Rejected",
      className: "rejected",
    },
  };


  const onStateChange = async (stateId) => {
    setAddressEditData(prev => ({
      ...prev,
      state: stateId,
      city: ""
    }));
    setErrors((prev) => {
      const nextErrors = clearErrorForField(prev, "state");
      return clearErrorForField(nextErrors, "city");
    });

    const res = await fetch(`https://api.164diecastzone.in/enduser/cities/?state_id=${stateId}`);
    const data = await res.json();
    setCities(data);
  };

  if (loading) return <div className="page">
    <div className="loading-wrap">
      <div className="loader" />
      <div>Loading profile...</div>
    </div>
  </div>;
  if (error) return <p style={{ padding: 20 }}>{error}</p>;
  if (!profileData) return null;
  const {
    personal_info,
    membership,
    points_balance,
    recent_orders = [],
    recent_preorders = [],
    wishlist_items,
    default_address,
  } = profileData;
  const referralCode = String(personal_info?.referral_code || "").trim();
  const hasReferralCode =
    referralCode &&
    !["none", "null", "undefined"].includes(referralCode.toLowerCase());

  return (
    <>
      {/* SAVE / CANCEL */}
      {saveError && (
        <p style={{ color: "#dc2626", marginTop: "10px", fontSize: "14px" }}>
          {saveError}
        </p>
      )}
      {toastMessage && (
        <div className="profile__toast">
          {toastMessage}
        </div>
      )}
      <div className="breadcrumb11">
        <div className="page-container">
          <span onClick={() => navigate("/")}>Home</span>
          <span className="highlight"> / </span>
          <span
            className="breadcrumb-link"
          >
            My ProfIile
          </span>
          <span className="highlight"> / </span>
        </div>
      </div>


      <div className="profile">
        <div className="profile__intro">
          <div className="profile__row">
            <div className="profile__name">
              MY <span className="profile__highlight">PROFILE</span>
              <br />
              {personal_info.last_name
                ? `${personal_info.first_name} ${personal_info.last_name}`
                : personal_info.first_name}
            </div>

            <img
              className="profile__image"
              src="https://files.idyllic.app/files/static/2618800"
              alt="profile_image"
            />
          </div>
        </div>

        <hr />
        <br />




        {/* ================= SUMMARY ================= */}
        <div className="profile__summary">

          <div className="profile__card">
            <h3 className="profile__card-title"><User size={18} /> Membership Details</h3>
            <div className="profile__list">
              <div className="profile__list-item"><span>Level:</span><span className="profile__badge">{membership ? membership.type : "Free"}</span></div>
              <div className="profile__list-item"><span>Join Date:</span> <span>{membership?.start_date || "—"}</span></div>
              <div className="profile__list-item"><span>Status:</span><span className="profile__status">{membership?.status || "Inactive"}</span></div>
            </div>
          </div>

          <div className="profile__card profile__card--disabled">
            <h3 className="profile__card-title">
              <Award size={18} /> Activity
            </h3>

            <div className="profile__empty-state">
              <p>Activity insights are coming soon ✨</p>
              <span>We’re preparing something meaningful for you</span>
            </div>
          </div>


          <div className="profile__card profile__card--center profile__card--highlight">
            <h3 className="profile__card-title">Your Rewards Balance</h3>

            <div className="profile__points-wrapper">
              <div className="profile__points">₹{points_balance}</div>
              <div className="profile__points-label">Loyalty Rewards</div>
            </div>

            {/* Referral */}
            {hasReferralCode && (
              <div className="profile__referral">
                <span className="profile__referral-label">Your Referral Code</span>

                <div className="profile__referral-box">
                  <span className="profile__referral-code">
                    {referralCode}
                  </span>
                  <button
                    className="profile__copy-btn"
                    onClick={() => handleCopy(referralCode)}
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}

            <button
              type="button"
              className="profile__claim-redeem-btn"
              onClick={() => navigate("/Redeempage")}
            >
              Claim Redeem
            </button>

            
          </div>


        </div>

        {/* ================= TABS ================= */}
        <div className="profile__tabs">
          <button className={activeTab === 'overview' ? 'profile__tab is-active' : 'profile__tab'} onClick={() => setActiveTab('overview')}><User size={16} />Overview</button>
          <button className={activeTab === 'orders' ? 'profile__tab is-active' : 'profile__tab'} onClick={() => setActiveTab('orders')}><Package size={16} />Orders</button>
          <button className={activeTab === 'favorites' ? 'profile__tab is-active' : 'profile__tab'} onClick={() => setActiveTab('favorites')}><Heart size={16} />Favorites Items</button>
        </div>

        {/* ================= OVERVIEW ================= */}
        {activeTab === 'overview' && (
          <div className="profile__section">
            <div className="profile__info">

              {/* HEADER + EDIT BUTTON */}
              <div className="profile__info-header">
                <h3 className="profile__info-title">
                  <User size={18} /> Personal Information
                </h3>

                {!isEditing && (
                  <button
                    className="profile__edit-btn"
                    onClick={() => {
                      setIsEditing(true);
                      setFieldErrors({});
                      setSaveError("");
                      setEditData({
                        first_name: personal_info.first_name || "",
                        last_name: personal_info.last_name || "",
                        email: personal_info.email || "",
                        phone_number: normalizePhoneForProfileDisplay(personal_info.phone_number),
                        date_of_birth: personal_info.date_of_birth || "",
                      });
                    }}

                  >
                    Edit
                  </button>
                )}
              </div>

              <p className="profile__subtitle">
                Manage your personal details and contact information
              </p>

              {/* GRID */}
              <div className="profile__grid">

                {/* First Name */}
                <div className="profile__field">
                  <div className="profile__field-header">
                    <label>First Name</label>
                    {isEditing && (
                      <span className="profile__field-count">
                        {(editData.first_name || "").length}/{MAX_NAME_LENGTH}
                      </span>
                    )}
                  </div>
                  <input
                    value={isEditing ? editData.first_name : personal_info.first_name}
                    readOnly={!isEditing}
                    maxLength={MAX_NAME_LENGTH}
                    onChange={(e) =>
                      handlePersonalFieldChange("first_name", e.target.value)
                    }
                  />
                  {fieldErrors.first_name && (
                    <p className="form-error" role="alert">
                      {fieldErrors.first_name}
                    </p>
                  )}
                </div>

                {/* Last Name */}
                <div className="profile__field">
                  <div className="profile__field-header">
                    <label>Last Name</label>
                    {isEditing && (
                      <span className="profile__field-count">
                        {(editData.last_name || "").length}/{MAX_NAME_LENGTH}
                      </span>
                    )}
                  </div>
                  <input
                    value={
                      isEditing
                        ? editData.last_name
                        : personal_info.last_name || "—"
                    }
                    readOnly={!isEditing}
                    maxLength={MAX_NAME_LENGTH}
                    onChange={(e) =>
                      handlePersonalFieldChange("last_name", e.target.value)
                    }
                  />
                  {fieldErrors.last_name && (
                    <p className="form-error" role="alert">
                      {fieldErrors.last_name}
                    </p>
                  )}
                </div>

                {/* Email (ALWAYS READ-ONLY) */}
                <div className="profile__field">
                  <label>Email</label>

                  <input
                    type={isEditing ? "email" : "text"}
                    value={
                      isEditing
                        ? editData.email
                        : personal_info.email || "—"
                    }
                    readOnly={!isEditing}
                    onChange={(e) => {
                      handlePersonalFieldChange("email", e.target.value);
                    }}
                  />

                  {fieldErrors.email && (
                    <p className="form-error" role="alert">
                      {fieldErrors.email}
                    </p>
                  )}


                </div>



                {/* Phone */}
                <div className="profile__field">
                  <label>Phone</label>
                  <input
                    value={
                      isEditing
                        ? editData.phone_number
                        : normalizePhoneForProfileDisplay(personal_info.phone_number) || "—"
                    }
                    readOnly={!isEditing}
                    inputMode="numeric"
                    maxLength={PHONE_LENGTH}
                    onChange={(e) =>
                      handlePersonalFieldChange("phone_number", e.target.value)
                    }
                  />
                  {fieldErrors.phone_number && (
                    <p className="form-error" role="alert">
                      {fieldErrors.phone_number}
                    </p>
                  )}
                </div>

                {/* Date of Birth */}
                <div className="profile__field profile__field--full">
                  <label>Date of Birth</label>
                  <input
                    type={isEditing ? "date" : "text"}
                    value={
                      isEditing
                        ? editData.date_of_birth
                        : personal_info.date_of_birth || "—"
                    }
                    readOnly={!isEditing}
                    onChange={(e) =>
                      handlePersonalFieldChange("date_of_birth", e.target.value)
                    }
                  />
                  {fieldErrors.date_of_birth && (
                    <p className="form-error" role="alert">
                      {fieldErrors.date_of_birth}
                    </p>
                  )}
                </div>
              </div>



              {isEditing && (
                <div className="profile__edit-actions">
                  <button
                    className="profile__button"
                    disabled={saving}
                    onClick={async () => {
                      const validationErrors = validatePersonalInfo(editData);

                      if (Object.keys(validationErrors).length > 0) {
                        setFieldErrors(validationErrors);
                        return;
                      }

                      setSaving(true);
                      setSaveError("");
                      setFieldErrors({});

                      try {

                        const data = await updateProfile(editData);


                        setProfileData(prev => ({
                          ...prev,
                          personal_info: data.personal_info,
                        }));

                        setIsEditing(false);

                        setToastMessage("Profile updated successfully ✨");


                        setTimeout(() => setToastMessage(""), 3000);

                      } catch (err) {
                        setSaveError("Unable to save changes. Please try again.");
                      } finally {
                        setSaving(false);
                      }
                    }}
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>


                  <button
                    className="profile__outline-btn"
                    onClick={() => {
                      setIsEditing(false);
                      setSaveError("");
                    }}
                  >
                    Cancel
                  </button>

                </div>
              )}
            </div>
            {/* ADDRESS INFORMATION */}
            <div className="profile__info">

              {/* HEADER + EDIT BUTTON */}
              <div className="profile__info-header">
                <h3 className="profile__info-title">
                  <MapPin size={18} />  Address Information
                </h3>

                {!isAddressEditing && (
                  <button
                    className="profile__edit-btn"
                    onClick={async () => {
                      setIsAddressEditing(true);
                      setErrors({});
                      setSaveError("");
                      const stateId = default_address?.state_id || "";

                      setAddressEditData({
                        address_line1: default_address?.address_line1 || "",
                        address_line2: default_address?.address_line2 || "",
                        landmark: default_address?.landmark || "",
                        state: stateId,
                        city: default_address?.city_id || "",
                        pincode: default_address?.pincode || "",
                      });

                      if (stateId) {
                        const res = await fetch(
                          `${BASE_URL}/enduser/cities/?state_id=${stateId}`
                        );
                        const data = await res.json();
                        setCities(data);
                      }
                    }}
                  >
                    Edit
                  </button>
                )}
              </div>

              <p className="profile__subtitle">
                Manage your delivery address and location details
              </p>

              {/* GRID */}
              <div className="profile__grid">

                <div className="profile__field profile__field--full">
                  <div className="profile__field-header">
                    <label>Address Line 1</label>
                    {isAddressEditing && (
                      <span className="profile__field-count">
                        {(addressEditData.address_line1 || "").length}/{ADDRESS_LINE_MAX_LENGTH}
                      </span>
                    )}
                  </div>
                  <input
                    value={
                      isAddressEditing
                        ? addressEditData.address_line1
                        : default_address?.address_line1 || "—"
                    }
                    readOnly={!isAddressEditing}
                    maxLength={ADDRESS_LINE_MAX_LENGTH}
                    onChange={(e) =>
                      handleAddressFieldChange("address_line1", e.target.value)
                    }
                  />
                  {errors.address_line1 && (
                    <p className="form-error" role="alert">
                      {errors.address_line1}
                    </p>
                  )}
                </div>

                <div className="profile__field profile__field--full">
                  <div className="profile__field-header">
                    <label>Address Line 2</label>
                    {isAddressEditing && (
                      <span className="profile__field-count">
                        {(addressEditData.address_line2 || "").length}/{ADDRESS_LINE_MAX_LENGTH}
                      </span>
                    )}
                  </div>
                  <input
                    value={
                      isAddressEditing
                        ? addressEditData.address_line2
                        : default_address?.address_line2 || "—"
                    }
                    readOnly={!isAddressEditing}
                    maxLength={ADDRESS_LINE_MAX_LENGTH}
                    onChange={(e) =>
                      handleAddressFieldChange("address_line2", e.target.value)
                    }
                  />
                  {errors.address_line2 && (
                    <p className="form-error" role="alert">
                      {errors.address_line2}
                    </p>
                  )}
                </div>

                <div className="profile__field">
                  <label>State</label>
                  {isAddressEditing ? (
                    <select
                      value={addressEditData.state}
                      onChange={(e) => onStateChange(e.target.value)}
                    >
                      <option value="">Select State</option>
                      {states.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    
                  ) : (
                    <input readOnly value={default_address?.state || "—"} />
                  )}
                  {errors.state && (
                    <p className="form-error" role="alert">
                      {errors.state}
                    </p>
                  )}
                </div>

                <div className="profile__field">
                  <label>City</label>
                  {isAddressEditing ? (
                    <select
                      value={addressEditData.city}
                      disabled={!addressEditData.state}
                      onChange={(e) =>
                        handleAddressFieldChange("city", e.target.value)
                      }
                    >
                      <option value="">Select City</option>
                      {cities.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  ) : (
                    <input readOnly value={default_address?.city || "—"} />
                  )}
                  {errors.city && (
                    <p className="form-error" role="alert">
                      {errors.city}
                    </p>
                  )}
                </div>

                <div className="profile__field">
                  <label>Pincode</label>
                  <input
                    value={
                      isAddressEditing
                        ? addressEditData.pincode
                        : default_address?.pincode || "—"
                    }
                    readOnly={!isAddressEditing}
                    inputMode="numeric"
                    maxLength={PINCODE_LENGTH}
                    onChange={(e) =>
                      handleAddressFieldChange("pincode", e.target.value)
                    }
                  />
                  {errors.pincode && (
                    <p className="form-error" role="alert">
                      {errors.pincode}
                    </p>
                  )}
                </div>

              </div>

              {/* ACTIONS */}
              {isAddressEditing && (
                <div className="profile__edit-actions">
                  <button
                    className="profile__button"
                    disabled={saving}
                    onClick={saveAddress}
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>


                  <button
                    className="profile__outline-btn"
                    onClick={() => {
                      setIsAddressEditing(false);
                      setErrors({});
                      setSaveError("");
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}

            </div>

          </div>
        )}


        {/* ================= ORDERS ================= */}
        {activeTab === "orders" && (
          <div className="profile__orders">
            <h2>Recent Orders</h2>

            {recent_orders.length === 0 ? (
              recent_preorders.length === 0 ? <p>No orders yet</p> : null
            ) : (
              recent_orders.map(order => {
                // 👇 PUT IT HERE
                const statusInfo =
                  ORDER_STATUS_MAP[order.status] || {
                    label: order.status,
                    className: "processing",
                  };

                return (
                  <div key={order.order_id} className="profile__order">
                    <div className="profile__order-left">
                      <div>
                        <h4>Order #{order.order_id}</h4>
                        <p>
                          {order.items_count} items • {order.date}
                        </p>
                      </div>

                      <span
                        className={`profile__status-badge ${statusInfo.className}`}
                      >
                        {statusInfo.label}
                      </span>
                    </div>

                    <div className="profile__order-right">
                      <span className="profile__amount">
                        ₹{order.total_amount}
                      </span>
                      <button
                        className="profile__outline-btn"
                        onClick={() => navigate(`/order-confirmation/${order.order_id}`)}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                );
              })
            )}

            {recent_preorders.map((order) => {
              const paymentStatus = String(order.payment_status || "pending").toLowerCase();
              const statusInfo =
                PREORDER_STATUS_MAP[paymentStatus] || {
                  label: order.payment_status || "Pending",
                  className: "processing",
                };

              return (
                <div key={`preorder-${order.preorder_order_id}`} className="profile__order">
                  <div className="profile__order-left">
                    <div>
                      <h4>Pre-Order #{order.preorder_order_id}</h4>
                      <p>
                        {order.items_count || 0} items • {order.date || "—"}
                      </p>
                    </div>

                    <span
                      className={`profile__status-badge ${statusInfo.className}`}
                    >
                      {statusInfo.label}
                    </span>
                  </div>

                  <div className="profile__order-right">
                    <span className="profile__amount">
                      ₹{order.total_amount}
                    </span>
                    <button
                      className="profile__outline-btn"
                      onClick={() =>
                        navigate(`/order-confirmation/${order.preorder_order_id}?type=preorder`)
                      }
                    >
                      View Details
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}


        {/* ================= FAVORITES (CONNECTED TO CONTEXT) ================= */}
        {activeTab === "favorites" && (
          <div className="profile__favorites">
            <h2>Favorite Items</h2>

            {wishlist_items.length === 0 ? (
              <p>No favorite items yet</p>
            ) : (
              <div className="profile__favorites-grid">
                {wishlist_items.map(item => (
                  <div key={item.id} className="profile__favorite-card">
                    <div className="profile__favorite-image">
                      <img
                        src={item.image || "/placeholder.png"}
                        alt={item.name}
                      />
                    </div>

                    <div className="profile__favorite-info">
                      <h4>{item.name}</h4>
                      <p className="profile__price">₹{item.price}</p>

                      <div className="profile__actions">
                        {isProductInCart(item.product_id) ? (
                          <button
                            className="profile__cart-btn profile__cart-btn--secondary"
                            onClick={() => navigate("/checkout")}
                          >
                            Go to Cart
                          </button>
                        ) : (
                          <button
                            className="profile__cart-btn"
                            onClick={() =>
                              updateCart({
                                type: "product",
                                id: item.product_id,
                                delta: 1,
                              })
                            }

                          >
                            Add to Cart
                          </button>
                        )}


                        <button
                          className="profile__icon-btn"
                          onClick={() => {
                            toggleFavorite(item.product_id);
                            setProfileData(prev => ({
                              ...prev,
                              wishlist_items: prev.wishlist_items.filter(
                                w => w.id !== item.id
                              ),
                            }))
                          }

                          }
                          title="Remove from wishlist"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}


        <a href="/">
          <button className="profile__fab"><Home size={24} /></button>
        </a>
      </div>
      {showCopied && (
        <div className="profile__toast profile__toast--near">
          Copied to clipboard
        </div>
      )}

    </>
  );
};

export default ProfilePage;
